import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Card,
	Chip,
	Divider,
	Grid,
	Grow,
	ListItemText,
	MenuItem,
	MenuList,
	Paper,
	Popper,
	Stack,
	Tab,
	Tabs,
	Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormType } from '@/api/enum/formType.enum';
import { useRef, useState } from 'react';
import AppFieldView from '@/common/components/form-view/AppFieldView';
import EstimatedAgeInfoHint from '@/pages/member-management/admission-request-detail/components/EstimatedAgeInfoHint';
import { formatAge } from '@/utils/ageHelper';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import useModal from '@/common/components/ConfirmationDialog/useModal';

import { useNavigate, useParams } from 'react-router-dom';
import {
	DISPLAY_DATE_FORMAT,
	DISPLAY_DATE_TIME_FORMAT,
	DISPLAY_DATETIME_12H_FORMAT,
} from '@/utils/dateHelper';
import dayjs from 'dayjs';
import UserInfoForm from './components/FamilyInfoForm';
import {
	useDeleteUserMutation,
	useGetUserDetailQuery,
} from '@/api/user-management/userManagementListApiSlice';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import {
	useDeleteFamilyMutation,
	useGetFamilyDetailQuery,
	useQueryFamilyGroupListQuery,
} from '@/api/family-management/familyApi';
import { showLabel } from '@/utils/helper';
import { LANGUAGE_LIST, YESNO } from '@/common/constants/common';
import { useGetAllEnumListQuery } from '@/api/common';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import CloseAllMemberShipDialog from './components/CloseAllMemberShipDialog';
import FamilyMemberFileNoHistoryButton from './components/FamilyMemberFileNoHistoryButton';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { useGetUser } from '@/hooks/useGetUser';
import { useTabParam } from '@/hooks/useTabParam';
import { FamilyMemberStatusEnum, PermissionEnum } from '@/api/enum/common.enum';
import LinkedRecordBanner from '@/common/components/identity-link/LinkedRecordBanner';
import ProgramAppLoginTab, {
	PROGRAM_APP_LOGIN_TAB,
	ProgramAppLoginTabLabel,
} from '@/common/components/program-app-login/ProgramAppLoginTab';

export default function FamilyDetailPage() {
	const { t } = useTranslation();
	const showModal = useModal();
	const [deleteFamily] = useDeleteFamilyMutation();
	const { familyId } = useParams();
	const navigate = useNavigate();
	const [currentTab, setCurrentTab] = useTabParam('detail');
	const [membershipButton, setMembershipButton] = useState(false);
	const [closeAllMembershipOpen, setCloseAllMembershipOpen] = useState(false);
	const anchorRef = useRef<HTMLDivElement>(null);

	const [serviceDetailsExpanded, setServiceDetailsExpanded] = useState(true);
	const [personalInfoExpanded, setPersonalInfoExpanded] = useState(true);
	const [occupationEducationExpanded, setOccupationEducationExpanded] =
		useState(true);
	const [
		personRecoveryInformationExpanded,
		setPersonRecoveryInformationExpanded,
	] = useState(true);
	const [memberShipExpanded, setMemberShipExpanded] = useState(true);
	const [internalRemarksExpanded, setInternalRemarksExpanded] = useState(true);
	const { userInfo } = useGetUser();

	const { data: details } = useGetFamilyDetailQuery(familyId || '', {
		refetchOnMountOrArgChange: true,
	});
	const { data: groupDetails } = useQueryFamilyGroupListQuery(familyId || '', {
		refetchOnMountOrArgChange: true,
	});

	// Close All Membership is only available to a family member whose status is
	// Admitted. This gate decides whether the menu item renders AND whether its
	// parent action button shows at all (an empty menu must not display its trigger).
	const canCloseAllMembership =
		!!userInfo?.permissionList?.includes(
			PermissionEnum.FamilyMemberMembershipActionButtonCloseAllMembership
		) && details?.data.familyMemberStatus === FamilyMemberStatusEnum.Admitted;

	// Only an in-progress (Admitted) family member has a Program App login account.
	const canSeeProgramAppLoginTab =
		!!userInfo?.permissionList?.includes(
			PermissionEnum.ViewAdmissionRequestAndMember
		) && details?.data.familyMemberStatus === FamilyMemberStatusEnum.Admitted;
	const activeTab =
		currentTab === PROGRAM_APP_LOGIN_TAB && !canSeeProgramAppLoginTab
			? 'detail'
			: currentTab;

	const handleDelete = async () => {
		showModal((props: any) => (
			<ConfirmationDialog
				{...props}
				showCloseIcon={true}
				title={t('member.deleteConfirmation')}
				titleProps={{ variant: 'h6' }}
				description={
					<Typography variant='body1'>{t('family.deleteText')}</Typography>
				}
				cancellationText={t('button.cancel')}
				confirmationText={t('button.confirm')}
				confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
				onConfirm={async () => {
					const response = await deleteFamily(familyId || '');
					if ('data' in response) {
						enqueueSnackbar(t('snackbar.deleteFamilySuccess'), {
							variant: 'warning',
							anchorOrigin: { horizontal: 'center', vertical: 'top' },
							autoHideDuration: 3000,
							action: (key) => {
								return <CloseIcon onClick={() => closeSnackbar(key)} />;
							},
						});

						navigate(`/family-management/family-management-list`, {
							replace: true,
						});
					}
				}}
			/>
		));
	};

	const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
		setCurrentTab(newValue);
	};

	const columns: GridColDef[] = [
		{
			field: 'targetMemberNameEn',
			headerName: t('member.name_en'),
			flex: 15,
		},
		{
			field: 'targetMemberNameZh',
			headerName: t('member.name_zh'),
			flex: 15,
		},
		{
			field: 'relationshipDesc',
			headerName: t('preAdmission.relationship'),
			flex: 25,
		},

		{
			field: 'targetMemberPhone',
			headerName: t('family.mobilePhone'),
			flex: 15,
		},
		{
			field: 'targetMemberFileNo',
			headerName: t('family.memberFileNo'),
			flex: 15,
			renderCell: (params) => (
				<a
					href={`/member-management/member-list/detail/${params.row.targetMemberIdDesc}`}
					target='_blank'
					rel='noopener noreferrer'
				>
					{params.row.targetMemberFileNo}
				</a>
			),
		},
		{
			field: 'targetFamilyMemberFileNo',
			headerName: t('family.familyMemberFileNo'),
			flex: 15,
			renderCell: (params) => (
				<a
					href={`/family-management/family-management-list/detail/${params.row.targetFamilyMemberIdDesc}`}
					target='_blank'
					rel='noopener noreferrer'
				>
					{params.row.targetFamilyMemberFileNo}
				</a>
			),
		},
		{
			field: 'targetFamilyMembership',
			headerName: t('family.membershipRole'),
			flex: 15,
		},
		{
			field: 'membershipStateDesc',
			headerName: t('member.isCase'),
			flex: 15,
			renderCell: (params) => showLabel(params.row.isCase, YESNO),
		},
	];

	return (
		<>
			<Box sx={{ width: '100%' }}>
				<Box display='flex' justifyContent='space-between'>
					<Typography variant='h4'>
						{t('drawerListItems.familyMemberDetails')}
					</Typography>
					<Stack direction={'row'} alignItems={'center'}>
						<Popper
							sx={{ zIndex: 1 }}
							open={membershipButton}
							anchorEl={anchorRef.current}
							role={undefined}
							transition
							disablePortal
						>
							{({ TransitionProps, placement }) => (
								<Grow
									{...TransitionProps}
									style={{
										transformOrigin:
											placement === 'bottom' ? 'center top' : 'center bottom',
									}}
								>
									<Paper>
										<MenuList id='split-button-menu' autoFocusItem>
											{canCloseAllMembership && (
												<MenuItem
													onClick={() => {
														setMembershipButton(false);
														setCloseAllMembershipOpen(true);
													}}
												>
													<ListItemText>
														{t('family.closeAllMemberShip')}
													</ListItemText>
												</MenuItem>
											)}
										</MenuList>
									</Paper>
								</Grow>
							)}
						</Popper>
						<CloseAllMemberShipDialog
							open={closeAllMembershipOpen}
							onClose={() => setCloseAllMembershipOpen(false)}
						/>
						{userInfo?.permissionList?.includes(
							PermissionEnum.FamilyMemberFormManagementTab
						) && (
							<Button
								endIcon={<AssignmentOutlinedIcon />}
								onClick={() => {
									navigate(
										`/family-management/family-management-list/detail/${familyId}/forms-management`
									);
								}}
								variant='contained'
								sx={{ marginRight: '20px' }}
							>
								{t('button.manageForms')}
							</Button>
						)}

						{canCloseAllMembership && (
							<Box ref={anchorRef}>
								<Button
									onClick={() =>
										setMembershipButton((prevOpen: any) => !prevOpen)
									}
									endIcon={<MoreVertOutlinedIcon />}
									variant='contained'
									sx={{ marginRight: '20px' }}
								>
									{t('button.membershipAction')}
								</Button>
							</Box>
						)}
						{userInfo?.permissionList?.includes(
							PermissionEnum.DeleteFamilyMember
						) && (
							<Button
								endIcon={<DeleteOutlined />}
								onClick={() => handleDelete()}
								variant='contained'
							>
								{t('button.delete')}
							</Button>
						)}
					</Stack>
				</Box>

				{details?.data.userId && (
					<LinkedRecordBanner
						linkedTo='member'
						fileNo={details.data.fileNo}
						href={
							userInfo?.permissionList?.includes(
								PermissionEnum.ViewAdmissionRequestAndMember
							)
								? `/member-management/member-list/detail/${details.data.userId}`
								: undefined
						}
					/>
				)}

				<Tabs
					value={activeTab}
					onChange={handleTabChange}
					sx={{ marginBottom: 3 }}
				>
					<Tab
						label={
							<Typography variant='body1'>{t('family.details')}</Typography>
						}
						value='detail'
						sx={{ textTransform: 'none' }}
					/>
					{userInfo?.permissionList?.includes(
						PermissionEnum.FamilyMemberManageFamilyMemberTab
					) && (
						<Tab
							label={
								<Typography variant='body1'>
									{t('family.manageFamilyMember')}
								</Typography>
							}
							value='family'
							sx={{ textTransform: 'none' }}
						/>
					)}
					{canSeeProgramAppLoginTab && (
						<Tab
							label={
								<ProgramAppLoginTabLabel
									entity='family-member'
									id={familyId || ''}
								/>
							}
							value={PROGRAM_APP_LOGIN_TAB}
							sx={{ textTransform: 'none' }}
						/>
					)}
				</Tabs>

				{activeTab == 'detail' && (
					<Card
						sx={{
							padding: ' 24px 24px 0',
							marginTop: 3,
						}}
					>
						<Accordion
							expanded={serviceDetailsExpanded}
							onChange={() =>
								setServiceDetailsExpanded(!serviceDetailsExpanded)
							}
							sx={{
								boxShadow: 'none!important',
								'&::before': {
									backgroundColor: 'transparent !important',
									border: 'none!important',
								},
								'& .MuiAccordionSummary-root': {
									padding: '0!important',
								},
								'& .MuiAccordionSummary-content': {
									margin: '0!important',
								},
								'& .MuiAccordionDetails-root': {
									padding: '0!important',
								},
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography
									variant='h6'
									component='span'
									sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
								>
									{t('member.serviceDetails')}
								</Typography>
								{userInfo?.permissionList?.includes(
									PermissionEnum.EditFamilyMember
								) && (
									<Button
										onClick={(e) => {
											e.stopPropagation();
											navigate(
												`/family-management/family-management-list/detail/${familyId}/edit-service-details`
											);
										}}
										sx={{ marginRight: '24px', textTransform: 'none' }}
										variant='outlined'
									>
										{t('button.edit')}
									</Button>
								)}
							</AccordionSummary>

							<AccordionDetails>
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid size={6}>
										<AppFieldView
											label={t('family.familyMemberFileNo')}
											value={details?.data.familyMemberFileNo}
											editButton={
												<FamilyMemberFileNoHistoryButton
													familyMemberId={familyId}
													fileNo={details?.data.familyMemberFileNo}
													hasFileNoHistory={details?.data.hasFileNoHistory}
												/>
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.fileNo')}
											value={details?.data.fileNo}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('common.unit')}
											value={details?.data.branchName}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.handledBy')}
											value={details?.data.handledByName}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.lastModified')}
											value={
												!details?.data.updatedAt
													? '-'
													: dayjs(details?.data.updatedAt)
															.locale('en')
															.format(DISPLAY_DATE_TIME_FORMAT)
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.status')}
											value={details?.data.familyMemberStatusDesc}
										/>
									</Grid>
									<Grid size={12}>
										<AppFieldView
											label={t('family.FWCmember')}
											value={
												details?.data.fwcMember == null
													? ''
													: details?.data.fwcMember
														? 'Yes'
														: 'No'
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.FSSmember')}
											value={details?.data.fssMember ? 'Yes' : 'No'}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.familyNetworkMember')}
											value={details?.data.familyNetworkMember ? 'Yes' : 'No'}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.acceptPhoneCalls')}
											value={details?.data.acceptPhoneCalls ? 'Yes' : 'No'}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.acceptMails')}
											value={details?.data.acceptMails ? 'Yes' : 'No'}
										/>
									</Grid>
									<Grid size={12}>
										<AppFieldView
											label={t('family.acceptEmails')}
											value={details?.data.acceptEmails ? 'Yes' : 'No'}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.acceptTWCcommunications')}
											value={
												details?.data.acceptTwcCommunications ? 'Yes' : 'No'
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.acceptFWCcommunications')}
											value={
												details?.data.acceptFwcCommunications == null
													? ''
													: details?.data.acceptFwcCommunications
														? 'Yes'
														: 'No'
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.acceptFSScommunications')}
											value={
												details?.data.acceptFssCommunications ? 'Yes' : 'No'
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.acceptFMNcommunications')}
											value={
												details?.data.acceptFamilyNetworkCommunications
													? 'Yes'
													: 'No'
											}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Divider sx={{ margin: '24px 0' }} />

						<Accordion
							expanded={personalInfoExpanded}
							onChange={() => setPersonalInfoExpanded(!personalInfoExpanded)}
							sx={{
								boxShadow: 'none!important',
								'&::before': {
									backgroundColor: 'transparent !important',
									border: 'none!important',
								},
								'& .MuiAccordionSummary-root': {
									padding: '0!important',
								},
								'& .MuiAccordionSummary-content': {
									margin: '0!important',
								},
								'& .MuiAccordionDetails-root': {
									padding: '0!important',
								},
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography
									variant='h6'
									sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
								>
									{t('member.personalInformation')}
								</Typography>
								{userInfo?.permissionList?.includes(
									PermissionEnum.EditFamilyMember
								) && (
									<Button
										onClick={(e) => {
											e.stopPropagation();
											navigate(
												`/family-management/family-management-list/detail/${familyId}/edit-personal`
											);
										}}
										sx={{ marginRight: '24px', textTransform: 'none' }}
										variant='outlined'
									>
										{t('button.edit')}
									</Button>
								)}
							</AccordionSummary>
							<AccordionDetails>
								{' '}
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.name_en')}
											value={details?.data.nameEn}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.name_zh')}
											value={details?.data.nameZh}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.dateOfBirth')}
											value={
												details?.data.dateOfBirth
													? dayjs(details?.data.dateOfBirth)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
													: ''
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.currentAge')}
											labelElement={
												details?.data.currentAgeEstimated && (
													<EstimatedAgeInfoHint
														currentAge={details?.data.currentAge}
													/>
												)
											}
											value={formatAge(
												details?.data.currentAge,
												details?.data.currentAgeEstimated
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.sex')}
											value={details?.data.sexDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.HKID')}
											value={details?.data.hkid}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.mobilePhone')}
											value={details?.data.phone}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.homeTel')}
											value={details?.data.homeTel}
										/>
									</Grid>
									<Grid size={12}>
										<AppFieldView
											label={t('member.email')}
											value={details?.data.email}
										/>
									</Grid>
									<Grid size={12}>
										<AppFieldView
											label={t('preAdmission.addressLanguage')}
											value={showLabel(
												details?.data.addressLanguage,
												LANGUAGE_LIST
											)}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.region')}
											value={details?.data.regionDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.district')}
											value={details?.data.districtDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.subDistrict')}
											value={details?.data.subdistrictDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.estate/court/village')}
											value={details?.data.estateCourtVillageDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.road/street')}
											value={details?.data.roadStreet}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.road/streetNo')}
											value={details?.data.roadStreetNo}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.building/block')}
											value={details?.data.buildingBlockDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.building/block(specify)')}
											value={details?.data.buildingBlockSpecify}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.floor')}
											value={details?.data.floor}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.flat')}
											value={details?.data.flat}
										/>
									</Grid>
									<Grid size={12}>
										<AppFieldView
											label={t('preAdmission.fullAddress')}
											value={details?.data.fullAddress}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Divider sx={{ margin: '24px 0' }} />

						<Accordion
							expanded={occupationEducationExpanded}
							onChange={() =>
								setOccupationEducationExpanded(!occupationEducationExpanded)
							}
							sx={{
								boxShadow: 'none!important',
								'&::before': {
									backgroundColor: 'transparent !important',
									border: 'none!important',
								},
								'& .MuiAccordionSummary-root': {
									padding: '0!important',
								},
								'& .MuiAccordionSummary-content': {
									margin: '0!important',
								},
								'& .MuiAccordionDetails-root': {
									padding: '0!important',
								},
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography
									variant='h6'
									sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
								>
									{t('family.occupationEducation')}
								</Typography>
								{userInfo?.permissionList?.includes(
									PermissionEnum.EditFamilyMember
								) && (
									<Button
										onClick={(e) => {
											e.stopPropagation();
											navigate(
												`/family-management/family-management-list/detail/${familyId}/edit-education`
											);
										}}
										sx={{ marginRight: '24px', textTransform: 'none' }}
										variant='outlined'
									>
										{t('button.edit')}
									</Button>
								)}
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid size={6}>
										<AppFieldView
											label={t('member.workStatus')}
											value={details?.data.familyMemberWorkStatusDesc}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											value={details?.data.familyMemberOccupationDesc}
											label={t('family.occupation')}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('preAdmission.education')}
											value={details?.data.educationDesc}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Divider sx={{ margin: '24px 0' }} />

						<Accordion
							expanded={personRecoveryInformationExpanded}
							onChange={() =>
								setPersonRecoveryInformationExpanded(
									!personRecoveryInformationExpanded
								)
							}
							sx={{
								boxShadow: 'none!important',
								'&::before': {
									backgroundColor: 'transparent !important',
									border: 'none!important',
								},
								'& .MuiAccordionSummary-root': {
									padding: '0!important',
								},
								'& .MuiAccordionSummary-content': {
									margin: '0!important',
								},
								'& .MuiAccordionDetails-root': {
									padding: '0!important',
								},
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography
									variant='h6'
									sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
								>
									{t('family.personRecoveryInformation')}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid size={12}>
										<AppFieldView
											label={t('family.isPIR')}
											value={details?.data.pirTypeDesc}
										/>
									</Grid>
									{details?.data.pirType == 2 && (
										<Grid size={12} display={'flex'}>
											<InfoOutlinedIcon />

											<Typography variant='body1'>
												{t('family.PIRtext')}
											</Typography>
										</Grid>
									)}

									{/* CHA-006 §7.2: Member PIRs show the snapshot taken when the form was submitted. */}
									{(details?.data.pirType === 0 || details?.data.pirType === 1) && (
										<>
											<Grid size={6}>
												<AppFieldView
													label={t('member.name_zh')}
													value={details?.data.pirNameZh}
												/>
											</Grid>
											<Grid size={6}>
												<AppFieldView
													label={t('member.sex')}
													value={details?.data.pirSexDesc}
												/>
											</Grid>
											<Grid size={12}>
												<AppFieldView
													label={t('member.dateOfBirth')}
													value={
														details?.data.pirDateOfBirth
															? dayjs(details?.data.pirDateOfBirth)
																	.locale('en')
																	.format(DISPLAY_DATE_FORMAT)
															: ''
													}
												/>
											</Grid>
											<Grid size={6}>
												<AppFieldView
													label={t('member.unit')}
													value={details?.data.pirUnit}
												/>
											</Grid>
											<Grid size={6}>
												<AppFieldView
													label={t('member.fileNo')}
													value={details?.data.pirFileNo}
												/>
											</Grid>
											<Grid size={6}>
												<AppFieldView
													label={t('family.relationshipToPIR')}
													value={details?.data.relationshipToPirDesc}
												/>
											</Grid>
											<Grid size={6}>
												<AppFieldView
													label={t('family.livingPIR')}
													value={details?.data.liveWithPir ? 'Yes' : 'No'}
												/>
											</Grid>
										</>
									)}
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Divider sx={{ margin: '24px 0' }} />

						<Accordion
							expanded={memberShipExpanded}
							onChange={() => setMemberShipExpanded(!memberShipExpanded)}
							sx={{
								boxShadow: 'none!important',
								'&::before': {
									backgroundColor: 'transparent !important',
									border: 'none!important',
								},
								'& .MuiAccordionSummary-root': {
									padding: '0!important',
								},
								'& .MuiAccordionSummary-content': {
									margin: '0!important',
								},
								'& .MuiAccordionDetails-root': {
									padding: '0!important',
								},
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography
									variant='h6'
									sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
								>
									{t('member.membershipInformation')}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid size={6}>
										<AppFieldView
											label={t('member.membershipRole')}
											value={details?.data.membershipRole}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.dateMembershipStart')}
											value={
												!details?.data.dateOfTwcMembershipStart
													? '-'
													: dayjs(details?.data.dateOfTwcMembershipStart)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.guardianNameEn')}
											value={details?.data.guardianName ?? undefined}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.guardianSignatureDateEn')}
											value={
												!details?.data.guardianSignatureDate
													? '-'
													: dayjs(details?.data.guardianSignatureDate)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.dateExitService')}
											value={
												!details?.data.dateOfExitService
													? '-'
													: dayjs(details?.data.dateOfExitService)
															.locale('en')
															.format(DISPLAY_DATE_FORMAT)
											}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('member.completeServiceExit')}
											value={details?.data.dateOfExitService ? 'Yes' : 'No'}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.exitReason')}
											value={details?.data.exitReason}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>

						<Divider sx={{ margin: '24px 0' }} />

						<Accordion
							expanded={internalRemarksExpanded}
							onChange={() =>
								setInternalRemarksExpanded(!internalRemarksExpanded)
							}
							sx={{
								boxShadow: 'none!important',
								'&::before': {
									backgroundColor: 'transparent !important',
									border: 'none!important',
								},
								'& .MuiAccordionSummary-root': {
									padding: '0!important',
								},
								'& .MuiAccordionSummary-content': {
									margin: '0!important',
								},
								'& .MuiAccordionDetails-root': {
									padding: '0!important',
								},
							}}
						>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography
									variant='h6'
									sx={{ width: '99%', flexShrink: 1, fontWeight: 'bold' }}
								>
									{t('family.internalRemarks')}
								</Typography>
								{userInfo?.permissionList?.includes(
									PermissionEnum.EditFamilyMember
								) && (
									<Button
										onClick={(e) => {
											e.stopPropagation();
											navigate(
												`/family-management/family-management-list/detail/${familyId}/edit-remarks`
											);
										}}
										sx={{ marginRight: '24px', textTransform: 'none' }}
										variant='outlined'
									>
										{t('button.edit')}
									</Button>
								)}
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={3} marginTop={0} marginBottom={3}>
									<Grid size={6}>
										<AppFieldView
											label={t('family.remarksAndPIR')}
											value={details?.data.pirRemarks}
										/>
									</Grid>
									<Grid size={6}>
										<AppFieldView
											label={t('family.remarks')}
											value={details?.data.applicantRemarks}
										/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>
					</Card>
				)}

				{activeTab == 'family' && (
					<Card
						sx={{
							padding: ' 24px 24px 0',
							marginTop: 3,
						}}
					>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={6}>
								<AppFieldView
									label={t('member.name_en')}
									value={details?.data.nameEn}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('member.name_zh')}
									value={details?.data.nameZh}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('family.familyMemberFileNo')}
									value={details?.data.familyMemberFileNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('family.memberFileNo')}
									value={details?.data.fileNo}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('family.membershipRole')}
									value={details?.data.membershipRole}
								/>
							</Grid>
							<Grid size={6}>
								<AppFieldView
									label={t('member.isCase')}
									value={showLabel(details?.data.isCase, YESNO)}
								/>
							</Grid>
						</Grid>

						<Grid
							container
							spacing={2}
							marginTop={0}
							marginBottom={2}
							alignItems='center'
						>
							<Grid size={12} display='flex'>
								<LocalDataGrid
									rows={groupDetails?.data || []}
									columns={columns}
									getRowId={(row) => row.id}
									pagination={false}
								/>
							</Grid>
						</Grid>
					</Card>
				)}

				{activeTab == PROGRAM_APP_LOGIN_TAB && (
					<ProgramAppLoginTab
						entity='family-member'
						id={familyId || ''}
						hasHkid={!!details?.data.hkid}
						hasPhone={!!details?.data.phone}
						canEdit={
							!!userInfo?.permissionList?.includes(
								PermissionEnum.EditAdmissionRequestAndMember
							)
						}
						linkedRecord={
							details?.data.userId
								? {
										linkedTo: 'member',
										fileNo: details.data.fileNo,
										href: userInfo?.permissionList?.includes(
											PermissionEnum.ViewAdmissionRequestAndMember
										)
											? `/member-management/member-list/detail/${details.data.userId}`
											: undefined,
									}
								: undefined
						}
					/>
				)}
			</Box>
		</>
	);
}
